import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { EventBus } from '@campus/core';

// Excalidraw pèse plusieurs Mo : chargé seulement à l'ouverture d'un tableau.
// Ses polices sont servies par Placio (public/excalidraw/fonts), jamais par un
// CDN : chemin posé ici, avant le chargement de la librairie (les imports
// d'un module passent avant son code).
const WhiteboardCanvas = lazy(() => {
  (window as unknown as { EXCALIDRAW_ASSET_PATH: string }).EXCALIDRAW_ASSET_PATH = '/excalidraw/';
  return import('./WhiteboardCanvas.js');
});

type OpenBoard = { itemId: string; name: string; claimId?: string; deskArchive?: boolean };

/**
 * WhiteboardHost — ouvre un tableau : celui d'un objet posé (« Ouvrir le
 * tableau », ZoneScene → ui:board-open), celui qu'un titulaire de bureau
 * partage, ou en lecture celui d'un duo ou de son bureau (« Mes tableaux blancs »).
 * Un seul tableau ouvert à la fois ; fermé quand le serveur retire l'accès.
 */
export function WhiteboardHost() {
  const [board, setBoard] = useState<OpenBoard | null>(null);
  const boardRef = useRef<OpenBoard | null>(null);
  boardRef.current = board;
  useEffect(() => {
    const open = (b: OpenBoard) => setBoard(b);
    const closed = ({ itemId }: { itemId: string }) => {
      const b = boardRef.current;
      if (!b || b.claimId || b.deskArchive || b.itemId !== itemId) return;
      setBoard(null);
      EventBus.emit('ui:toast', { message: 'Le tableau partagé s’est fermé : tu n’es plus dans le bureau, ou le partage a été retiré.', tone: 'info' });
    };
    EventBus.on('ui:board-open', open);
    EventBus.on('board:closed', closed);
    EventBus.on('board:unshared', closed);
    return () => {
      EventBus.off('ui:board-open', open);
      EventBus.off('board:closed', closed);
      EventBus.off('board:unshared', closed);
    };
  }, []);
  if (!board) return null;
  return (
    <Suspense fallback={<div className="pl-whiteboard pl-whiteboard-loading">Ouverture du tableau…</div>}>
      <WhiteboardCanvas
        key={`${board.itemId}:${board.claimId ?? ''}:${board.deskArchive ? 'desk' : ''}`}
        itemId={board.itemId} name={board.name} claimId={board.claimId} deskArchive={board.deskArchive}
        onClose={() => setBoard(null)}
      />
    </Suspense>
  );
}
